import {
  Stethoscope, Activity, Droplet, Syringe, HeartPulse, ShieldCheck,
  Baby, UserCheck, Thermometer, ClipboardList, FileText, Pill, Users
} from "lucide-react";

export const SERVICES = [
  { slug: "consultant-physician", title: "Consultant Physician", icon: Stethoscope,
    short: "Comprehensive adult medical care with an experienced family physician.",
    overview: "Complete physical evaluation, diagnosis and long-term management for adult health concerns — from common illnesses to complex chronic conditions.",
    benefits: ["Personalized treatment plans","Long-term relationship with your physician","Ethical prescription practices"],
    symptoms: ["Fatigue, weakness","Unexplained weight change","Recurrent infections","Persistent pain"] },
  { slug: "general-medicine", title: "General Medicine", icon: HeartPulse,
    short: "Everyday medical care for the whole family.",
    overview: "Evaluation and treatment for common infections, digestive issues, respiratory concerns, allergies and more — with clear, evidence-based guidance.",
    benefits: ["Same-day appointments","Rational prescriptions","Home care advice"],
    symptoms: ["Cold, cough, sore throat","Stomach upset","Body ache","Skin issues"] },
  { slug: "diabetes-management", title: "Diabetes Management", icon: Droplet,
    short: "Blood sugar control, diet plans and complication screening.",
    overview: "Structured diabetes care with HbA1c tracking, medication titration, dietary counselling, and annual foot / eye / kidney checks.",
    benefits: ["Reduced complications","Simple lifestyle plans","Family involvement"],
    symptoms: ["Excessive thirst / urination","Fatigue","Slow-healing wounds","Blurred vision"] },
  { slug: "hypertension-management", title: "Hypertension Management", icon: Activity,
    short: "Blood pressure control that protects your heart, kidneys and brain.",
    overview: "Accurate BP monitoring, lifestyle-first approach, and appropriate medications when needed. Regular follow-up to prevent silent damage.",
    benefits: ["Lower stroke risk","Kidney protection","Simple monitoring routines"],
    symptoms: ["Often silent","Occasional headaches","Chest heaviness","Vision changes"] },
  { slug: "nebulization-and-iv", title: "Nebulization & IV Medication", icon: Syringe,
    short: "In-clinic nebulizer and IV support for acute needs.",
    overview: "Clean, monitored administration of nebulization and IV medications for asthma attacks, severe dehydration and infections.",
    benefits: ["Rapid symptom relief","Sterile technique","Close observation"],
    symptoms: ["Breathlessness","Severe cough","Dehydration","High fever"] },
  { slug: "preventive-healthcare", title: "Preventive Health Checkups", icon: ShieldCheck,
    short: "Head-to-toe checkups tailored to your age and family history.",
    overview: "Age-appropriate screening packages covering blood work, ECG, BP, blood sugar, lipid profile, kidney and liver function, thyroid, and lifestyle review.",
    benefits: ["Catch issues early","Personalized advice","Cost-effective packages"],
    symptoms: ["Family history of disease","40+ age screening","Executive health"] },
  { slug: "pediatrics-vaccination", title: "Pediatrics & Vaccination", icon: Baby,
    short: "Compassionate care for children with a gentle approach.",
    overview: "Common childhood illnesses, growth checks, and IAP-schedule vaccinations. Advice to worried parents included at no extra charge.",
    benefits: ["Gentle exam approach","Vaccination reminders","Parent counselling"],
    symptoms: ["Fever in children","Ear pain","Diarrhoea","Growth concerns"] },
  { slug: "womens-health", title: "Women's Health", icon: UserCheck,
    short: "Respectful, private consultations for women of all ages.",
    overview: "PCOS, thyroid, anemia, menstrual concerns, menopause guidance, and preventive screening. Referrals to trusted specialists when needed.",
    benefits: ["Private consultation","Empathetic approach","Continuity of care"],
    symptoms: ["Irregular cycles","Fatigue","Weight gain","Mood changes"] },
  { slug: "geriatric-care", title: "Geriatric Care", icon: Users,
    short: "Kind, patient-centred care for our elders.",
    overview: "Comprehensive management of multiple chronic conditions common in older adults — with focus on quality of life, medication safety and dignity.",
    benefits: ["Medication review","Fall-risk assessment","Family caregiver support"],
    symptoms: ["Multiple medicines","Memory concerns","Weakness / falls"] },
  { slug: "fever-and-infection", title: "Fever & Infection Treatment", icon: Thermometer,
    short: "Accurate diagnosis, targeted treatment — no unnecessary antibiotics.",
    overview: "Careful evaluation of fevers, appropriate tests when needed, and antibiotic stewardship. Because your future health matters too.",
    benefits: ["Antibiotic stewardship","Fast recovery","Clear home-care guidance"],
    symptoms: ["High fever","Chills","Body ache","Sore throat"] },
  { slug: "medical-certificates", title: "Medical Certificates", icon: FileText,
    short: "Genuine fitness and sickness certificates.",
    overview: "Ethical issuance of medical certificates only after proper evaluation. Occupational health experience (AFIH-certified).",
    benefits: ["Proper documentation","Ethical practice","Occupational health expertise"],
    symptoms: ["Fitness / sick leave certification","Pre-employment checkup"] },
  { slug: "health-counselling", title: "Health Counselling", icon: ClipboardList,
    short: "Lifestyle change, one honest conversation at a time.",
    overview: "Weight, sleep, stress, smoking cessation and dietary counselling delivered with warmth and realistic expectations.",
    benefits: ["Sustainable habits","Family-friendly plans","Long-term follow-up"],
    symptoms: ["Weight concerns","Poor sleep","Stress","Smoking / addiction"] },
];

export const DIAGNOSTICS = [
  { name: "Blood Sugar (F/PP)", desc: "Fasting and post-meal glucose." },
  { name: "HbA1c", desc: "3-month average glucose control." },
  { name: "Complete Blood Count (CBC)", desc: "Screens for anemia, infection, immunity." },
  { name: "Lipid Profile", desc: "Cholesterol, triglycerides, HDL, LDL." },
  { name: "Liver Function Test (LFT)", desc: "Liver enzymes and bilirubin." },
  { name: "Kidney Function Test (KFT)", desc: "Urea, creatinine, electrolytes." },
  { name: "Thyroid Profile (TSH/T3/T4)", desc: "Screens for hypo/hyper-thyroid." },
  { name: "Urine Routine", desc: "Detects infections and kidney issues." },
  { name: "ECG (Electrocardiogram)", desc: "Simple, quick heart rhythm test." },
  { name: "Preventive Health Package", desc: "Comprehensive annual screening bundle." },
];

export const WHY_US = [
  { title: "35+ Years of Experience", icon: "clock" },
  { title: "Former AGM (Medical), BHEL", icon: "award" },
  { title: "Trusted Family Physician", icon: "heart" },
  { title: "Personalized Healthcare", icon: "user-check" },
  { title: "Affordable Consultation", icon: "wallet" },
  { title: "Ethical Medical Practice", icon: "scale" },
  { title: "Modern Diagnostic Support", icon: "activity" },
  { title: "Clean & Hygienic Clinic", icon: "sparkles" },
  { title: "Easy Online Appointment", icon: "calendar" },
  { title: "Teleconsultation Available", icon: "video" },
];
